import { useRef } from 'react';
import { TyRadioGroup } from '@tymbe/ty-components/inputs/radio';
import { BadgePillRadio } from '@tymbe/ty-components/inputs/radio/badgePill';
import { Form, FormApi, Input } from 'informed';
import { TySelect, TyTextArea } from '@tymbe/ty-components/inputs';
import { useQuery, useQueryClient } from 'react-query';
import feathersClient from '../../../../apiClient';
import { BlockReasons, BlockReasons as BlockReasonsEnum } from '@tymbe/schema/enums';
import { BlockReasonDisplay, Roles } from '../../../../utils/enums';
import { useUser } from '../../../../apiClient/ApiContext';
import { BlockReasonsData } from '@tymbe/schema/src/block-reasons.interface';
import apiClient from '../../../../apiClient';
import { PrimaryButton } from '@tymbe/ui-primitives';
import { ErrorAlert, SuccessAlert } from '../../../../components/alerts';
import getResponseArray from '@tymbe/utils/getResponseArray';
import { informedConfig } from '../../../../config/informed';

type BlockTymberProps = {
  applicationId: number;
  setModalOpen: (arg: boolean) => void;
  onSubmit?: (values: BlockTymberFormValues) => void;
};

export type BlockTymberFormValues = {
  company_id: number;
  branchoffice_id?: number | null;
  blocks_radio: number;
  reason: {
    value: BlockReasonsData;
    label: string;
  };
  note: string | null;
  person_id: number;
};

const BlockTymber = ({ applicationId, setModalOpen, onSubmit }: BlockTymberProps) => {
  const user = useUser();
  const formApiRef = useRef<FormApi<BlockTymberFormValues>>(undefined);
  const formApi = formApiRef.current;
  const qClient = useQueryClient();

  const { data: application, isLoading } = useQuery(
    'shiftWithCompany',
    async () =>
      apiClient.service('application').get(applicationId, {
        query: {
          $eager: 'shift.[company, branchoffice.parent]',
        },
      }),
    { enabled: !!applicationId }
  );

  const isTymbe = user.hasRoles([Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN, Roles.ADMIN, Roles.TYMBE_COORDINATOR]);

  const { data: blockOptions } = useQuery(['blockOptions'], async () => {
    const res = await feathersClient.service('block-reasons').find({
      query: {
        $and: [...(!isTymbe ? [{ id: { $ne: BlockReasonsEnum.PREVENTION } }] : [])],
      },
    });
    return getResponseArray(res);
  });

  const selectOptions =
    blockOptions?.map((blockReasonOption: { id: BlockReasons; severity: number }) => ({
      value: {
        id: blockReasonOption.id,
        severity: blockReasonOption.severity,
      },
      label: BlockReasonDisplay[blockReasonOption.id],
    })) || [];

  const onSubmitFallback = async (inputData: BlockTymberFormValues) => {

    const newBlockedPerson = {
      company_id: inputData.company_id,
      person_id: inputData.person_id || Number(application?.person_id),
      note: inputData.note || null,
      branchoffice_id: inputData.branchoffice_id || null,
      reason: inputData.reason.value.id,
      severity: Number(inputData.reason.value.severity),
    };
    if (newBlockedPerson.reason === BlockReasons.OTHER && !newBlockedPerson.note) {
      ErrorAlert('V případě důvodu "Jiné" je potřeba vyplnit poznámku blokace!');
      return;
    }

    try {
      await feathersClient.service('company-blocked-user').create(newBlockedPerson, {
        query: { $cancelApplications: true, $notify: true },
      });
      setModalOpen(false);
      await qClient.invalidateQueries(['company-blocked-user']);
      await qClient.invalidateQueries(['shift', 'application', Number(application?.shift?.id)])
      SuccessAlert('Nová blokace vytvořena');
    } catch (err) {
      ErrorAlert('Při vytváření blokace došlo k chybě');
    }
  };

  const handleSubmit = ({ values }: { values: BlockTymberFormValues }) => {
    return onSubmit !== undefined ? onSubmit(values) : onSubmitFallback(values);
  };

  return (
    <Form<BlockTymberFormValues> onSubmit={handleSubmit} formApiRef={formApiRef} className="flex flex-col flex-1">
      {!isLoading && (
        <>
          <div className="flex flex-col p-4 bg-secondary-transparent-5 gap-6 flex-1">
            <div className="flex flex-col gap-2">
              <span className="text-body-2-xl text-secondary-500">Chci blokovat pro</span>
              <Input name="person_id" type="hidden" initialValue={application?.person_id} />
              <Input name="company_id" type="hidden" initialValue={application?.shift?.company_id} />
              <Input name="branchoffice_id" type="hidden" />

              <TyRadioGroup
                name="blocks_radio"
                className="flex max-w-full flex-col gap-2 md:flex-row"
                required={informedConfig.errorMessage.required}
              >
                <BadgePillRadio
                  className="truncate mx-0"
                  sizeVariant="L"
                  label={`Firmu ${application?.shift?.company?.name}`}
                  value={`${application?.shift?.company_id}`}
                  onClick={() => formApi?.setValue(`branchoffice_id`, null)}
                  disabled={user.hasRoles([Roles.BRANCHOFFICE_MANAGER])}
                />
                <BadgePillRadio
                  className="truncate mx-0"
                  sizeVariant="L"
                  label={`Provozovnu ${application?.shift?.branchoffice?.parent?.name}`}
                  value={`${application?.shift?.branchoffice?.parent_id}`}
                  onClick={() => formApi?.setValue(`branchoffice_id`, application?.shift?.branchoffice?.parent_id)}
                />
                <BadgePillRadio
                  className="truncate mx-0"
                  sizeVariant="L"
                  label={`Oddělení ${application?.shift?.branchoffice?.name}`}
                  value={`${application?.shift?.branchoffice_id}`}
                  onClick={() =>
                    formApi?.setValue(`branchoffice_id`, application?.shift?.branchoffice_id)
                  }
                />
              </TyRadioGroup>
            </div>

            <div className="flex flex-col gap-4">
              <div className="">
                <span className="text-caption-xl text-secondary-500">Důvod</span>
                <TySelect
                  name="reason"
                  placeholder="Vyberte ze seznamu"
                  className="bg-white rounded-lg"
                  options={selectOptions}
                  required={informedConfig.errorMessage.required}
                />
              </div>
              <div>
                <span className="text-caption-xl text-secondary-500">Poznámka</span>
                <TyTextArea className="max-h-40" placeholder="Poznámka k blokaci" name="note" />
              </div>
            </div>
          </div>
          <div className="p-4 flex justify-end">
            <PrimaryButton type="submit" className="bg-red-500 text-white">
              Ukončit spolupráci
            </PrimaryButton>
          </div>
        </>
      )}
    </Form>
  );
};

export default BlockTymber;
